import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  TemplateRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';

import { AttachmentSizeDisplayComponent } from '@shared/components';
import { FileUploadType } from '@shared/enums';
import { DprDetailsGridBaseComponent } from '@dpr-details/models/grid';
import { TextDisplayComponent, TextEditComponent } from '@grid/components';
import { GridSortMode } from '@grid/enums';
import {
  CellDisplayConfig,
  GridColumn,
  GridOptions,
  TooltipContext,
} from '@grid/models';
import { FileService } from '@shared/services/files';
import { RegistrationsComponent } from '@dpr-details/models/component';
import {
  GridColumns,
  GridColumnTemplates,
  GridDefaultLabels,
} from '@shared/utils/grid';
import { FileUploadConfig } from '@shared/models';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';

@UntilDestroy()
@Component({
  selector: 'app-dpr-attachments-grid',
  templateUrl: './dpr-attachments-grid.component.html',
  styleUrls: ['./dpr-attachments-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprAttachmentsGridComponent
  extends DprDetailsGridBaseComponent<DprAttachmentsGridData>
  implements RegistrationsComponent<DprAttachmentsGridData>, OnChanges
{
  //#region Inputs

  @Input({ required: true }) isAppOnline: boolean;
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) fileUploadConfig: FileUploadConfig;
  @Input({ required: true }) isAppendix: boolean;
  @Input({ required: true }) isNameConventionEnabled: boolean;
  @Input({ required: true }) shouldDisplayPrintInReport: boolean;
  @Input({ required: true }) enableStickyDirective: boolean;

  //#endregion

  //#region Templates

  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate: TemplateRef<any>;

  @ViewChild('addButtonTemplate', { static: true })
  addButtonTemplate: TemplateRef<any>;

  //#endregion

  addButtonLabel: string;

  constructor(
    private fileService: FileService,
    private dprAttachmentService: DprAttachmentService
  ) {
    super();
  }

  //#region Lifecycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fileUploadConfig?.currentValue) {
      this.addButtonLabel = $localize`:@@dprDetails.dprAttachmentsGrid.btnUploadANew:Upload - ${this.fileUploadConfig.sizeLimitMessage}:sizeLimit:`;
    }

    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.isAppOnline || changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  //#endregion

  onDownload(data: DprAttachmentsGridData): void {
    const fileName = this.dprAttachmentService.addExtension(
      data.displayName,
      data.extension
    );

    this.fileService.download(data.externalUrl, fileName, data.type);
  }

  onFilesChanged(files: File[]): void {
    if (!files?.length) {
      return;
    }

    files.forEach((file) => {
      this.fileService
        .uploadFile(file, FileUploadType.Attachment, this.clientId, false)
        .pipe(
          untilDestroyed(this),
          tap((response) => {
            const dprAttachment =
              this.dprAttachmentService.mapFileToDprAttachmentGridData(
                response.file,
                response.url
              );

            this.gridComponent.api.addRows(dprAttachment);
          })
        )
        .subscribe();
    });
  }

  isRowEmpty(data: DprAttachmentsGridData): boolean {
    return (
      String.isNullOrWhiteSpace(data.name) &&
      String.isNullOrWhiteSpace(data.type) &&
      String.isNullOrWhiteSpace(data.extension)
    );
  }

  protected getGridOptions(): GridOptions<DprAttachmentsGridData> {
    return {
      labeling: GridDefaultLabels,
      addConfig: {
        value: {
          comment: '',
          isPrintInReport: true,
        },
        allowAdding: this.isAppOnline && this.canEdit,
        buttonTemplate: this.addButtonTemplate,
        keepAutogeneratedIds: true,
        disableAddShortcut: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: this.canEdit,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: this.canEdit,
        allowSelection: this.canEdit,
        isEmpty: this.isRowEmpty.bind(this),
      },
      sortConfig: {
        mode: GridSortMode.ColumnHeader,
        allowSorting: true,
      },
      selectionConfig: {
        allowSelection: this.canEdit,
      },
      headerConfig: {
        enableStickyDirective: this.enableStickyDirective,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprAttachmentsGridData>[] {
    const columns = [
      {
        columnDef: this.isNameConventionEnabled ? 'displayName' : 'name',
        header: {
          name: $localize`:@@dprDetails.dprAttachmentsGrid.colName:File name`,
        },
        display: { type: TextDisplayComponent },
        edit: {
          type: TextEditComponent,
          config: {
            options: {
              maxLength: 190,
            },
          },
        },
        allowEditing: this.isNameConventionEnabled ? false : this.canEdit,
        allowSorting: true,
        minWidth: 120,
      },
      {
        columnDef: 'extension',
        header: {
          name: $localize`:@@dprDetails.dprAttachmentsGrid.colType:Type`,
        },
        display: {
          type: TextDisplayComponent,
        },
        allowEditing: false,
        allowSorting: true,
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 100,
      },
      {
        columnDef: 'size',
        header: {
          name: $localize`:@@dprDetails.dprAttachmentsGrid.colSize:Size`,
        },
        display: {
          type: AttachmentSizeDisplayComponent,
        },
        allowEditing: false,
        allowSorting: true,
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 120,
      },
      {
        ...GridColumns.comment,
        allowEditing: this.canEdit,
        allowSorting: true,
      },
      {
        ...GridColumns.actions,
        display: { type: this.actionsTemplate },
        width: 114,
      },
    ];

    if (this.shouldDisplayPrintInReport) {
      const isPrintInReportColumn: GridColumn<DprAttachmentsGridData> = {
        ...GridColumnTemplates.checkbox,
        columnDef: 'isPrintInReport',
        header: {
          ...GridColumnTemplates.checkbox.header,
          name: $localize`:@@dprDetails.dprAttachmentsGrid.colIsPrintInReport:Print in report`,
        },
        display: {
          ...(GridColumnTemplates.checkbox
            .display as CellDisplayConfig<DprAttachmentsGridData>),
          allowEditing: (data: DprAttachmentsGridData) =>
            this.isAppendix ? this.canEdit : this.canEdit && data.isImage,
        },
        allowSorting: true,
        width: 130,
        tooltip: {
          ...GridColumnTemplates.checkbox.tooltip,
          message: $localize`:@@dprDetails.dprAttachmentsGrid.txtOnlyImagesCanBePrinted:File types other than images cannot be printed in the report`,
          disabled: (context: TooltipContext<DprAttachmentsGridData>) =>
            this.isAppendix ? true : context.data.isImage,
        },
      };

      columns.insert(isPrintInReportColumn, columns.length - 1);
    }

    return columns;
  }
}
